import type { CreatorUrlType } from "./buildCreatorPublicUrl";
import type { CreatorBrowsableUrlPrefs } from "./getCreatorPublicBaseUrl";

function stripHost(value: string): string {
  return String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
}

function isBrowsableApex(domain: string): boolean {
  const d = domain.toLowerCase();
  return d === "browsable.ai" || d === "browsable.bio";
}

function readMemberPrefs(username: string): {
  urlType: CreatorUrlType | null;
  domain: string | null;
} {
  try {
    const raw = localStorage.getItem("member");
    if (!raw) return { urlType: null, domain: null };
    const parsed = JSON.parse(raw) as {
      username?: string;
      url_type?: CreatorUrlType | null;
      domain?: string | null;
    };
    if (String(parsed?.username ?? "").trim() !== username) {
      return { urlType: null, domain: null };
    }
    return {
      urlType:
        parsed.url_type === "subdomain" || parsed.url_type === "path"
          ? parsed.url_type
          : null,
      domain: parsed.domain ? String(parsed.domain).trim() : null,
    };
  } catch (_e) {
    return { urlType: null, domain: null };
  }
}

export function resolveSelectedCreatorUrlPrefs(
  username: string,
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined
): { urlType: CreatorUrlType; domain: string } {
  const u = String(username || "").trim();
  let urlType = browsableUrlPrefs?.urlType ?? null;
  let domain = browsableUrlPrefs?.domain
    ? String(browsableUrlPrefs.domain).trim()
    : null;

  if ((!urlType || !domain) && u) {
    const member = readMemberPrefs(u);
    urlType = urlType ?? member.urlType;
    domain = domain ?? member.domain;
  }

  return {
    urlType: urlType === "subdomain" || urlType === "path" ? urlType : "path",
    domain: stripHost(domain || "browsable.ai") || "browsable.ai",
  };
}

/**
 * Public creator base from DomainControls selection only.
 * Does NOT prefer window.location (localhost / vercel / current host).
 * Use for dashboard copy/share previews of the creator's real public URL.
 *
 * Priority: active custom domain → saved url_type+domain → browsable.ai/{username}
 */
export function getSelectedCreatorPublicBaseUrl(args: {
  username: string;
  customDomain?: string | null | undefined;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined;
}): string {
  const username = String(args.username || "").trim();
  if (!username) return "";

  const activeCustom = stripHost(String(args.customDomain || ""));
  if (activeCustom) {
    return `https://${activeCustom}`;
  }

  const { urlType, domain } = resolveSelectedCreatorUrlPrefs(
    username,
    args.browsableUrlPrefs
  );

  // user.domain saved as a custom host string
  if (domain && !isBrowsableApex(domain)) {
    return `https://${domain}`;
  }

  const apex = isBrowsableApex(domain) ? domain : "browsable.ai";
  if (urlType === "subdomain") {
    return `https://${encodeURIComponent(username)}.${apex}`;
  }
  return `https://${apex}/${encodeURIComponent(username)}`;
}

/**
 * Embed base from DomainControls selection (no current-host rewrite).
 * - Custom domain: https://{custom}/embed
 * - Browsable path/subdomain prefs: https://{domain}/embed/{username}
 *   (path-style on apex for stable third-party embeds)
 */
export function getSelectedCreatorEmbedBaseUrl(args: {
  username: string;
  customDomain?: string | null | undefined;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined;
}): string {
  const username = String(args.username || "").trim();
  if (!username) return "";

  const activeCustom = stripHost(String(args.customDomain || ""));
  if (activeCustom) {
    return `https://${activeCustom}/embed`;
  }

  const { domain } = resolveSelectedCreatorUrlPrefs(
    username,
    args.browsableUrlPrefs
  );

  if (domain && !isBrowsableApex(domain)) {
    return `https://${domain}/embed`;
  }

  const apex = isBrowsableApex(domain) ? domain : "browsable.ai";
  return `https://${apex}/embed/${encodeURIComponent(username)}`;
}

/** Host-only display string for UI prefixes (no protocol). */
export function getSelectedCreatorPublicBaseDisplay(args: {
  username: string;
  customDomain?: string | null | undefined;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined;
}): string {
  return getSelectedCreatorPublicBaseUrl(args)
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
}

/** Collection / route URL from DomainControls selection (no current-host rewrite). */
export function buildSelectedCreatorCollectionUrl(args: {
  username: string;
  route?: string | null;
  customDomain?: string | null | undefined;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null | undefined;
}): string {
  const username = String(args.username || "").trim();
  if (!username) return "";

  const route = String(args.route || "").replace(/^\/+/, "").replace(/\/+$/, "");
  const base = getSelectedCreatorPublicBaseUrl({
    username,
    customDomain: args.customDomain,
    browsableUrlPrefs: args.browsableUrlPrefs,
  }).replace(/\/+$/, "");

  if (!base) return "";
  return route ? `${base}/${route}` : base;
}
